import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@/ui';

const MAX_FEEDBACK_LENGTH = 1000;

export type FeedbackModalDirection = 'positive' | 'negative';

export interface FeedbackModalProps {
  direction: FeedbackModalDirection | null;
  error?: string | null;
  isSubmitting?: boolean;
  open: boolean;
  onClose: () => void;
  onSubmit: (feedbackText: string) => void;
}

const getPlaceholder = (direction: FeedbackModalDirection | null): string => {
  if (direction === 'positive') {
    return 'What works well?';
  }
  if (direction === 'negative') {
    return 'What could be improved or should be fixed?';
  }
  return '';
};

export const FeedbackModal = ({
  direction,
  error = null,
  isSubmitting = false,
  open,
  onClose,
  onSubmit,
}: FeedbackModalProps) => {
  const [feedbackText, setFeedbackText] = useState('');

  useEffect(() => {
    if (open) {
      setFeedbackText('');
    }
  }, [open]);

  return (
    <Dialog maxWidth="xs" open={open} fullWidth onClose={onClose}>
      <DialogTitle>Feedback</DialogTitle>
      <DialogContent>
        <TextField
          disabled={isSubmitting}
          error={Boolean(error)}
          inputProps={{ maxLength: MAX_FEEDBACK_LENGTH }}
          maxRows={8}
          minRows={3}
          placeholder={getPlaceholder(direction)}
          value={feedbackText}
          autoFocus
          fullWidth
          multiline
          onChange={(event) => setFeedbackText(event.target.value)}
        />
        {error ? (
          <Alert severity="error" style={{ marginTop: 16 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button
          color="gray"
          disabled={isSubmitting}
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={isSubmitting}
          variant="contained"
          onClick={() => onSubmit(feedbackText)}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
